// AES 加密算法
export class AES {
  // 初始化
  public constructor(key: Uint8Array) {
    this.secretKey = new Uint8Array(key);

    if (![16, 24, 32].includes(this.secretKey.length)) {
      throw new TypeError("invalid key size (must be 16, 24 or 32 bytes)");
    }

    this.initSBox();
    this.initInverseSBox();
    this.expandKey();
  }

  // 密钥
  private readonly secretKey: Uint8Array;

  // S盒
  private sBox = new Array<number>(256);

  // 逆S盒
  private invSBox = new Array<number>(256);

  // 循环密钥
  private roundKeys: Array<Array<number>> = [];

  // 有限域乘法
  private galoisMultiply(first: number, second: number): number {
    let result = 0;
    let multiplicand = first;
    let multiplier = second;
    for (let bit = 0; bit < 8; bit++) {
      if (multiplier & 1) {
        result ^= multiplicand;
      }
      const highBitSet = multiplicand & 0x80;
      multiplicand <<= 1;
      if (highBitSet) {
        multiplicand ^= 0x1b;
      }
      multiplier >>= 1;
    }
    return result & 0xff;
  }

  // 有限域求逆
  private gf256Inverse(input: number) {
    let result = 1;
    let base = input;
    let exponent = 254;

    while (exponent > 0) {
      if (exponent & 1) {
        result = this.galoisMultiply(result, base);
      }
      base = this.galoisMultiply(base, base);
      exponent >>= 1;
    }

    return result;
  }

  // 仿射变换
  private affineTransform(x: number) {
    let y = 0;

    for (let i = 0; i < 8; i++) {
      let b = 0;
      b ^= (x >> ((i + 0) % 8)) & 1;
      b ^= (x >> ((i + 4) % 8)) & 1;
      b ^= (x >> ((i + 5) % 8)) & 1;
      b ^= (x >> ((i + 6) % 8)) & 1;
      b ^= (x >> ((i + 7) % 8)) & 1;
      b ^= (0x63 >> i) & 1;

      y |= b << i;
    }

    return y;
  }

  // 初始化S盒
  private initSBox() {
    for (let i = 0; i < 256; i++) {
      const inv = i === 0 ? 0 : this.gf256Inverse(i);
      this.sBox[i] = this.affineTransform(inv);
    }
  }

  // 初始化逆S盒
  private initInverseSBox() {
    for (let i = 0; i < 256; i++) {
      this.invSBox[this.sBox[i]] = i;
    }
  }

  // 密钥扩展
  private expandKey() {
    const keyLength = this.secretKey.length;
    const rounds = keyLength / 4 + 6;
    const expandedKeySize = 16 * (rounds + 1);

    const expandedKey = new Uint8Array(expandedKeySize);
    expandedKey.set(this.secretKey);

    const rcon = [0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36];
    let rconIndex = 0;

    for (let i = keyLength; i < expandedKeySize; i += 4) {
      const temp = expandedKey.slice(i - 4, i);

      if (i % keyLength === 0) {
        const rotated = [temp[1], temp[2], temp[3], temp[0]];
        for (let j = 0; j < 4; j++) {
          temp[j] = this.sBox[rotated[j]];
        }
        temp[0] ^= rcon[rconIndex];
        rconIndex += 1;
      } else if (keyLength === 32 && i % keyLength === 16) {
        for (let j = 0; j < 4; j++) {
          temp[j] = this.sBox[temp[j]];
        }
      }

      for (let j = 0; j < 4; j++) {
        expandedKey[i + j] = expandedKey[i + j - keyLength] ^ temp[j];
      }
    }

    this.roundKeys = [];
    for (let i = 0; i < rounds + 1; i++) {
      const roundKey = [];
      for (let j = 0; j < 4; j++) {
        const start = i * 16 + j * 4;
        roundKey.push(
          (expandedKey[start] << 24) |
            (expandedKey[start + 1] << 16) |
            (expandedKey[start + 2] << 8) |
            expandedKey[start + 3],
        );
      }
      this.roundKeys.push(roundKey);
    }
  }

  // 添加轮密钥
  private addRoundKey(state: Array<number>, roundKey: Array<number>) {
    for (let i = 0; i < 4; i++) {
      state[i] ^= roundKey[i];
    }
  }

  // 字节代换
  private subBytes(state: Array<number>) {
    for (let i = 0; i < 4; i++) {
      state[i] =
        (this.sBox[(state[i] >> 24) & 0xff] << 24) |
        (this.sBox[(state[i] >> 16) & 0xff] << 16) |
        (this.sBox[(state[i] >> 8) & 0xff] << 8) |
        this.sBox[state[i] & 0xff];
    }
  }

  // 逆字节代换
  private invSubBytes(state: Array<number>) {
    for (let i = 0; i < 4; i++) {
      state[i] =
        (this.invSBox[(state[i] >> 24) & 0xff] << 24) |
        (this.invSBox[(state[i] >> 16) & 0xff] << 16) |
        (this.invSBox[(state[i] >> 8) & 0xff] << 8) |
        this.invSBox[state[i] & 0xff];
    }
  }

  // 行移位
  private shiftRows(state: Array<number>) {
    const s0 = state[0];
    const s1 = state[1];
    const s2 = state[2];
    const s3 = state[3];

    state[0] = (s0 & 0xff000000) | (s1 & 0x00ff0000) | (s2 & 0x0000ff00) | (s3 & 0x000000ff);
    state[1] = (s1 & 0xff000000) | (s2 & 0x00ff0000) | (s3 & 0x0000ff00) | (s0 & 0x000000ff);
    state[2] = (s2 & 0xff000000) | (s3 & 0x00ff0000) | (s0 & 0x0000ff00) | (s1 & 0x000000ff);
    state[3] = (s3 & 0xff000000) | (s0 & 0x00ff0000) | (s1 & 0x0000ff00) | (s2 & 0x000000ff);
  }

  // 逆行移位
  private invShiftRows(state: Array<number>) {
    const s0 = state[0];
    const s1 = state[1];
    const s2 = state[2];
    const s3 = state[3];

    state[0] = (s0 & 0xff000000) | (s3 & 0x00ff0000) | (s2 & 0x0000ff00) | (s1 & 0x000000ff);
    state[1] = (s1 & 0xff000000) | (s0 & 0x00ff0000) | (s3 & 0x0000ff00) | (s2 & 0x000000ff);
    state[2] = (s2 & 0xff000000) | (s1 & 0x00ff0000) | (s0 & 0x0000ff00) | (s3 & 0x000000ff);
    state[3] = (s3 & 0xff000000) | (s2 & 0x00ff0000) | (s1 & 0x0000ff00) | (s0 & 0x000000ff);
  }

  // 列混淆
  private mixColumns(state: Array<number>) {
    for (let i = 0; i < 4; i++) {
      const s = state[i];
      const s0 = (s >> 24) & 0xff;
      const s1 = (s >> 16) & 0xff;
      const s2 = (s >> 8) & 0xff;
      const s3 = s & 0xff;

      state[i] =
        ((this.galoisMultiply(s0, 2) ^ this.galoisMultiply(s1, 3) ^ s2 ^ s3) << 24) |
        ((s0 ^ this.galoisMultiply(s1, 2) ^ this.galoisMultiply(s2, 3) ^ s3) << 16) |
        ((s0 ^ s1 ^ this.galoisMultiply(s2, 2) ^ this.galoisMultiply(s3, 3)) << 8) |
        (this.galoisMultiply(s0, 3) ^ s1 ^ s2 ^ this.galoisMultiply(s3, 2));
    }
  }

  // 逆列混淆
  private invMixColumns(state: Array<number>) {
    for (let i = 0; i < 4; i++) {
      const s = state[i];
      const s0 = (s >> 24) & 0xff;
      const s1 = (s >> 16) & 0xff;
      const s2 = (s >> 8) & 0xff;
      const s3 = s & 0xff;

      state[i] =
        ((this.galoisMultiply(s0, 14) ^
          this.galoisMultiply(s1, 11) ^
          this.galoisMultiply(s2, 13) ^
          this.galoisMultiply(s3, 9)) <<
          24) |
        ((this.galoisMultiply(s0, 9) ^
          this.galoisMultiply(s1, 14) ^
          this.galoisMultiply(s2, 11) ^
          this.galoisMultiply(s3, 13)) <<
          16) |
        ((this.galoisMultiply(s0, 13) ^
          this.galoisMultiply(s1, 9) ^
          this.galoisMultiply(s2, 14) ^
          this.galoisMultiply(s3, 11)) <<
          8) |
        (this.galoisMultiply(s0, 11) ^
          this.galoisMultiply(s1, 13) ^
          this.galoisMultiply(s2, 9) ^
          this.galoisMultiply(s3, 14));
    }
  }

  // 字节数组转状态
  private bytesToState(bytes: Uint8Array): Array<number> {
    const state = [];
    for (let i = 0; i < 4; i++) {
      state.push(
        (bytes[i * 4] << 24) |
          (bytes[i * 4 + 1] << 16) |
          (bytes[i * 4 + 2] << 8) |
          bytes[i * 4 + 3],
      );
    }
    return state;
  }

  // 状态转字节数组
  private stateToBytes(state: Array<number>): Uint8Array {
    const bytes = new Uint8Array(16);
    for (let i = 0; i < 4; i++) {
      bytes[i * 4] = (state[i] >> 24) & 0xff;
      bytes[i * 4 + 1] = (state[i] >> 16) & 0xff;
      bytes[i * 4 + 2] = (state[i] >> 8) & 0xff;
      bytes[i * 4 + 3] = state[i] & 0xff;
    }
    return bytes;
  }

  // 加密 16字节明文
  public encrypt(plaintext: Uint8Array): Uint8Array {
    if (plaintext.length !== 16) {
      throw new TypeError("invalid plaintext size (must be 16 bytes)");
    }

    const state = this.bytesToState(plaintext);
    const rounds = this.roundKeys.length - 1;

    this.addRoundKey(state, this.roundKeys[0]);

    for (let round = 1; round < rounds; round++) {
      this.subBytes(state);
      this.shiftRows(state);
      this.mixColumns(state);
      this.addRoundKey(state, this.roundKeys[round]);
    }

    this.subBytes(state);
    this.shiftRows(state);
    this.addRoundKey(state, this.roundKeys[rounds]);

    return this.stateToBytes(state);
  }

  // 解密 16字节密文
  public decrypt(ciphertext: Uint8Array): Uint8Array {
    if (ciphertext.length !== 16) {
      throw new TypeError("invalid ciphertext size (must be 16 bytes)");
    }

    const state = this.bytesToState(ciphertext);
    const rounds = this.roundKeys.length - 1;

    this.addRoundKey(state, this.roundKeys[rounds]);
    this.invShiftRows(state);
    this.invSubBytes(state);

    for (let round = rounds - 1; round > 0; round--) {
      this.addRoundKey(state, this.roundKeys[round]);
      this.invMixColumns(state);
      this.invShiftRows(state);
      this.invSubBytes(state);
    }

    this.addRoundKey(state, this.roundKeys[0]);

    return this.stateToBytes(state);
  }
}

// 字符串转 Uint8Array
export function utf8ToUint8Array(text: string): Uint8Array {
  const charCodeList: Array<number> = [];

  for (let i = 0; i < text.length; i += 1) {
    const charCode = text.charCodeAt(i);

    if (charCode < 0x80) {
      charCodeList.push(charCode);
    } else if (charCode < 0x800) {
      charCodeList.push(0xc0 | (charCode >> 6));
      charCodeList.push(0x80 | (charCode & 0x3f));
    } else if ((charCode & 0xfc00) === 0xd800) {
      if (i + 1 < text.length) {
        const lowSurrogate = text.charCodeAt(i + 1);
        if ((lowSurrogate & 0xfc00) === 0xdc00) {
          const codePoint = 0x10000 + ((charCode & 0x3ff) << 10) + (lowSurrogate & 0x3ff);
          charCodeList.push(0xf0 | (codePoint >> 18));
          charCodeList.push(0x80 | ((codePoint >> 12) & 0x3f));
          charCodeList.push(0x80 | ((codePoint >> 6) & 0x3f));
          charCodeList.push(0x80 | (codePoint & 0x3f));
          i += 1;
        } else {
          charCodeList.push(0xef, 0xbf, 0xbd);
        }
      } else {
        charCodeList.push(0xef, 0xbf, 0xbd);
      }
    } else if ((charCode & 0xfc00) === 0xdc00) {
      charCodeList.push(0xef, 0xbf, 0xbd);
    } else {
      charCodeList.push(0xe0 | (charCode >> 12));
      charCodeList.push(0x80 | ((charCode >> 6) & 0x3f));
      charCodeList.push(0x80 | (charCode & 0x3f));
    }
  }

  return new Uint8Array(charCodeList);
}

// Uint8Array 转字符串
export function uint8ArrayToUtf8(bytes: Uint8Array): string {
  const charList: Array<string> = [];
  let index = 0;

  while (index < bytes.length) {
    const byte1 = bytes[index];

    if (byte1 < 0x80) {
      charList.push(String.fromCharCode(byte1));
      index += 1;
      continue;
    }

    if ((byte1 & 0xe0) === 0xc0 && index + 1 < bytes.length) {
      const byte2 = bytes[index + 1];
      if ((byte2 & 0xc0) === 0x80) {
        const charCode = ((byte1 & 0x1f) << 6) | (byte2 & 0x3f);
        charList.push(String.fromCharCode(charCode));
        index += 2;
        continue;
      }
    }

    if ((byte1 & 0xf0) === 0xe0 && index + 2 < bytes.length) {
      const byte2 = bytes[index + 1];
      const byte3 = bytes[index + 2];
      if ((byte2 & 0xc0) === 0x80 && (byte3 & 0xc0) === 0x80) {
        const charCode = ((byte1 & 0x0f) << 12) | ((byte2 & 0x3f) << 6) | (byte3 & 0x3f);
        charList.push(String.fromCharCode(charCode));
        index += 3;
        continue;
      }
    }

    if ((byte1 & 0xf8) === 0xf0 && index + 3 < bytes.length) {
      const byte2 = bytes[index + 1];
      const byte3 = bytes[index + 2];
      const byte4 = bytes[index + 3];
      const validBytes =
        (byte2 & 0xc0) === 0x80 && (byte3 & 0xc0) === 0x80 && (byte4 & 0xc0) === 0x80;

      if (validBytes) {
        const codePoint =
          ((byte1 & 0x07) << 18) | ((byte2 & 0x3f) << 12) | ((byte3 & 0x3f) << 6) | (byte4 & 0x3f);

        if (codePoint >= 0x10000 && codePoint <= 0x10ffff) {
          const adjusted = codePoint - 0x10000;
          const highSurrogate = 0xd800 + (adjusted >> 10);
          const lowSurrogate = 0xdc00 + (adjusted & 0x3ff);
          charList.push(String.fromCharCode(highSurrogate, lowSurrogate));
          index += 4;
          continue;
        }
      }
    }

    charList.push("\ufffd");
    index += 1;
  }

  return charList.join("");
}

// Uint8Array 转 base64
export function uint8ArrayToBase64(uint8Array: Uint8Array): string {
  const base64Chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  let base64 = "";
  let buffer = 0;
  let bits = 0;
  for (const byte of uint8Array) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 6) {
      base64 += base64Chars[(buffer >> (bits -= 6)) & 0x3f];
    }
  }
  if (bits > 0) base64 += base64Chars[(buffer << (6 - bits)) & 0x3f];
  return base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
}

// base64 转 Uint8Array
export function base64ToUint8Array(base64: string): Uint8Array {
  const base64Chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  const base64Values = base64.replace(/[=]/gu, "");
  const uint8Array = new Uint8Array((base64Values.length * 6) >> 3);
  let buffer = 0;
  let bits = 0;
  let byteIndex = 0;
  for (const char of base64) {
    if (char === "=") break;
    buffer = (buffer << 6) | base64Chars.indexOf(char);
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      uint8Array[byteIndex] = (buffer >> bits) & 0xff;
      byteIndex += 1;
    }
  }
  return uint8Array;
}

// PKCS7 填充
export function pkcs7Pad(data: Uint8Array): Uint8Array {
  const padder = 16 - (data.length % 16);

  const result = new Uint8Array(data.length + padder);
  result.set(data);

  for (let i = data.length; i < result.length; i++) {
    result[i] = padder;
  }

  return result;
}

// PKCS7 去除
export function pkcs7Strip(data: Uint8Array): Uint8Array {
  if (data.length < 16) {
    throw new TypeError("PKCS#7 invalid length");
  }

  const padder = data[data.length - 1];
  if (padder === 0 || padder > 16) {
    throw new TypeError("PKCS#7 padding byte out of range");
  }

  const length = data.length - padder;
  for (let i = 0; i < padder; i++) {
    if (data[length + i] !== padder) {
      throw new TypeError("PKCS#7 invalid padding byte");
    }
  }

  return new Uint8Array(data.subarray(0, length));
}

// CBC 模式加密
export function encryptCBC(key: Uint8Array, iv: Uint8Array, plaintext: Uint8Array): Uint8Array {
  if (iv.length !== 16) {
    throw new TypeError("invalid iv size (must be 16 bytes)");
  }
  if (plaintext.length % 16 !== 0) {
    throw new TypeError("invalid plaintext size (must be multiple of 16 bytes)");
  }

  const aes = new AES(key);
  const state: { iv: Uint8Array; lastBlock: Uint8Array } = {
    iv: new Uint8Array(iv),
    lastBlock: new Uint8Array(iv),
  };

  const ciphertext = new Uint8Array(plaintext.length);
  for (let i = 0; i < plaintext.length; i += 16) {
    for (let j = 0; j < 16; j++) {
      state.lastBlock[j] ^= plaintext[i + j];
    }
    state.lastBlock = aes.encrypt(state.lastBlock);
    ciphertext.set(state.lastBlock, i);
  }
  return ciphertext;
}

// CBC 模式解密
export function decryptCBC(key: Uint8Array, iv: Uint8Array, ciphertext: Uint8Array): Uint8Array {
  if (iv.length !== 16) {
    throw new TypeError("invalid iv size (must be 16 bytes)");
  }
  if (ciphertext.length % 16 !== 0) {
    throw new TypeError("invalid ciphertext size (must be multiple of 16 bytes)");
  }
  const aes = new AES(key);
  const state: { iv: Uint8Array; lastBlock: Uint8Array } = {
    iv: new Uint8Array(iv),
    lastBlock: new Uint8Array(iv),
  };
  const plaintext = new Uint8Array(ciphertext.length);
  for (let i = 0; i < ciphertext.length; i += 16) {
    const block = aes.decrypt(ciphertext.subarray(i, i + 16));
    for (let j = 0; j < 16; j++) {
      plaintext[i + j] = block[j] ^ state.lastBlock[j];
      state.lastBlock[j] = ciphertext[i + j];
    }
  }
  return plaintext;
}
